<template>
  <div class="page-container reveal">
    <el-card class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">Annuaire Clients</h2>
          <p class="page-subtitle">{{ clients.length }} compte{{ clients.length !== 1 ? 's' : '' }} enregistrés</p>
        </div>
        <div class="header-actions">
          <el-input
            v-model="searchQ"
            placeholder="Nom, téléphone..."
            class="w-64"
            @input="load"
          >
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          
          <el-select v-model="activeFilter" class="w-36" @change="load">
            <el-option label="Tous" value="" />
            <el-option label="Actifs" value="true" />
            <el-option label="Bloqués" value="false" />
          </el-select>

          <el-button :icon="Download" @click="handleExportCsv" title="Exporter les clients en fichier Excel/CSV">
            Exporter CSV
          </el-button>
          <el-button @click="load" :loading="isLoading" circle :icon="Refresh" />
        </div>
      </div>

      <div v-if="selectedRows.length && canManageClients" class="bulk-bar">
        <span class="bulk-count">{{ selectedRows.length }} sélectionné(s)</span>
        <el-button type="danger" size="small" :loading="bulkLoading" @click="bulkToggle(false)">Bloquer</el-button>
        <el-button type="success" size="small" :loading="bulkLoading" @click="bulkToggle(true)">Débloquer</el-button>
      </div>

      <el-table :data="paged" v-loading="isLoading" class="custom-table" stripe @selection-change="onSelectionChange">
        <el-table-column type="selection" width="45" />
        <el-table-column label="Identité Client" min-width="200" sortable :sort-method="(a, b) => (a.full_name || '').localeCompare(b.full_name || '')">
          <template #default="{ row }">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-white/5 border border-profissa-glass-border flex items-center justify-center font-bold text-profissa-accent">
                {{ (row.full_name || 'C')[0].toUpperCase() }}
              </div>
              <div>
                <div class="font-bold text-[0.95rem]">{{ row.full_name || '—' }}</div>
                <div class="text-xs text-profissa-text-muted font-mono mt-0.5">{{ formatPhone(row.phone) }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="Vérification" width="160">
          <template #default="{ row }">
            <el-tag :type="row.phone_verified ? 'success' : 'warning'" class="uppercase tracking-wider font-bold">
              <span class="flex items-center gap-1">
                <el-icon v-if="row.phone_verified"><Check /></el-icon>
                {{ row.phone_verified ? 'Tél Vérifié' : 'En attente' }}
              </span>
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="Statut" width="140" sortable :sort-method="(a, b) => Number(a.is_active) - Number(b.is_active)">
          <template #default="{ row }">
            <div class="px-3 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wide inline-block"
                 :class="row.is_active ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'">
              {{ row.is_active ? 'Compte Actif' : 'Suspendu' }}
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="Inscription" width="160" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
          <template #default="{ row }">
            <span class="text-profissa-text-muted text-sm">{{ fmtDate(row.created_at) }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="Gérer" width="160" fixed="right" align="center">
          <template #default="{ row }">
            <div class="flex gap-1 justify-center">
              <el-button size="small" text :icon="View" @click="openHistory(row)" title="Historique" />
              <el-button
                v-if="canManageClients"
                :type="row.is_active ? 'danger' : 'success'"
                link
                size="small"
                @click="handleToggle(row)"
                class="font-bold tracking-wide"
              >
                {{ row.is_active ? 'Bloquer' : 'Débloquer' }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="flex justify-end pt-6 mt-4 border-t border-profissa-glass-border">
        <el-pagination
          v-if="clients.length > pageSize"
          v-model:current-page="page"
          v-model:page-size="pageSize"
          layout="total, prev, pager, next"
          :total="clients.length"
          @size-change="page = 1"
        />
      </div>
    </el-card>

    <!-- ── Historique escrow client ── -->
    <el-drawer append-to-body
      v-model="showHistory"
      :title="historyClient ? `Escrow — ${historyClient.full_name || formatPhone(historyClient.phone)}` : 'Historique'"
      size="520px"
      direction="rtl"
    >
      <div v-if="isLoadingHistory" class="flex justify-center py-12">
        <el-icon class="is-loading text-3xl text-profissa-accent"><Loading /></el-icon>
      </div>
      <el-empty v-else-if="!escrowRows.length" description="Aucune transaction d'escrow" />
      <div v-else class="flex flex-col gap-3 px-2">
        <div class="flex justify-between text-sm font-bold mb-2 px-1 text-profissa-text-muted">
          <span>Total libéré :</span>
          <span class="text-profissa-accent">{{ totalPaid.toLocaleString('fr-FR') }} FCFA</span>
        </div>
        <div v-for="row in escrowRows" :key="row.id" class="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-profissa-glass-border text-sm">
          <div>
            <el-tag size="small" :type="row.type === 'diagnostic' ? 'info' : 'primary'">
              {{ row.type === 'diagnostic' ? 'Diagnostic' : 'Travaux' }}
            </el-tag>
            <div class="text-xs text-profissa-text-muted mt-1 font-mono">#{{ row.intervention_id?.slice(0,8) }}…</div>
          </div>
          <div class="text-right">
            <div class="font-bold">{{ row.amount?.toLocaleString('fr-FR') }} FCFA</div>
            <el-tag size="small" :type="escrowTagType(row.status)">{{ escrowStatusLabel(row.status) }}</el-tag>
          </div>
          <div class="text-xs text-profissa-text-dim ml-2">{{ fmtDate(row.created_at) }}</div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, Check, View, Loading, Download } from '@element-plus/icons-vue'
import { listClients, toggleClientActive, getClientEscrowHistory } from '../services/adminApi'
import { extractApiError } from '../api'
import { useAdminPermissions } from '../composables/useAdminPermissions'
import { formatPhone } from '../utils/format'
import { exportToCsv } from '../utils/exportCsv'

const selectedRows  = ref([])
const clients       = ref([])
const isLoading     = ref(false)
const searchQ       = ref('')
const activeFilter  = ref('')
const page          = ref(1)
const pageSize      = ref(15)

const handleExportCsv = () => {
  const columns = [
    { key: 'id', label: 'ID Client' },
    { key: 'full_name', label: 'Nom complet', format: (v) => v || '—' },
    { key: 'phone', label: 'Téléphone', format: (v) => formatPhone(v) },
    { key: 'phone_verified', label: 'Tél Vérifié', format: (v) => v ? 'Oui' : 'Non' },
    { key: 'is_active', label: 'Statut Compte', format: (v) => v ? 'Actif' : 'Suspendu' },
    { key: 'created_at', label: 'Date Inscription', format: (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '' },
  ]
  const dateStr = new Date().toISOString().slice(0, 10)
  exportToCsv(`clients_profissa_${dateStr}.csv`, columns, clients.value)
}

// ── History drawer ──
const showHistory      = ref(false)
const historyClient    = ref(null)
const escrowRows       = ref([])
const isLoadingHistory = ref(false)
const totalPaid = computed(() => escrowRows.value.filter(r => r.status === 'released').reduce((s, r) => s + (r.amount || 0), 0))
const { hasPermission } = useAdminPermissions()
const canManageClients = computed(() => hasPermission('clients.manage.any'))
const escrowTagType    = (s) => ({ held: 'warning', released: 'success', refunded: 'info' }[s] || 'info')
const escrowStatusLabel = (s) => ({ held: 'Séquestré', released: 'Libéré', refunded: 'Remboursé' }[s] || s)

const openHistory = async (row) => {
  historyClient.value = row
  escrowRows.value = []
  showHistory.value = true
  isLoadingHistory.value = true
  try { escrowRows.value = await getClientEscrowHistory(row.id) }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoadingHistory.value = false }
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'
const paged = computed(() => clients.value.slice((page.value - 1) * pageSize.value, page.value * pageSize.value))
const onSelectionChange = (rows) => { selectedRows.value = rows }
const bulkLoading = ref(false)

const bulkToggle = async (activate) => {
  if (!canManageClients.value) return
  const targets = selectedRows.value.filter(r => r.is_active !== activate)
  if (!targets.length) { ElMessage.warning('Aucun compte à modifier.'); return }
  try {
    await ElMessageBox.confirm(`${activate ? 'Débloquer' : 'Bloquer'} ${targets.length} client(s) ?`, 'Action groupée', { type: 'warning' })
    bulkLoading.value = true
    let ok = 0
    for (const row of targets) { try { await toggleClientActive(row.id); ok++ } catch { /* skip */ } }
    ElMessage.success(`${ok} client(s) ${activate ? 'débloqué(s)' : 'bloqué(s)'}.`)
    await load()
  } catch { /* cancelled */ }
  finally { bulkLoading.value = false }
}

const load = async () => {
  isLoading.value = true
  try {
    const active = activeFilter.value === '' ? null : activeFilter.value === 'true'
    clients.value = await listClients(searchQ.value, active)
    page.value = 1
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoading.value = false }
}

const handleToggle = async (row) => {
  if (!canManageClients.value) return
  try {
    await ElMessageBox.confirm(
      row.is_active ? 'Suspendre l\'accès de ce client ?' : 'Rétablir l\'accès de ce client ?',
      'Action Requise',
      { confirmButtonText: 'Confirmer', cancelButtonText: 'Annuler', type: 'warning' }
    )
    const res = await toggleClientActive(row.id)
    ElMessage.success(res.is_active ? 'Compte réactivé.' : 'Compte suspendu.')
    await load()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(extractApiError(e))
  }
}

onMounted(load)
</script>

<style scoped>
.page-container { @apply h-full flex flex-col; }
.main-list-card { @apply flex-1 flex flex-col; }
.list-header { @apply flex justify-between items-end pb-8; }
.page-title { @apply text-2xl font-black mb-1; }
.page-subtitle { @apply text-profissa-text-muted text-sm font-semibold; }
.header-actions { @apply flex gap-3 items-center; }
.bulk-bar { @apply flex items-center gap-3 px-4 py-3 mb-4 rounded-xl; background: rgba(255, 107, 0, 0.06); border: 1px solid rgba(255, 107, 0, 0.15); }
.bulk-count { @apply font-extrabold text-sm text-profissa-accent mr-auto; }
</style>
